import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';
import { CURRENT_PUBLIC_POLICY_IDENTITY } from '../../shared/publicPolicyIdentity';
import accountDeletionSource from './policies/account-deletion.md?raw';
import parentNoticeSource from './policies/parent-notice.md?raw';
import privacySource from './policies/privacy.md?raw';
import termsSource from './policies/terms.md?raw';
import trackingDisclosureSource from './policies/tracking-disclosure.md?raw';

export type PolicySlug =
  'privacy' | 'terms' | 'parent-notice' | 'tracking-disclosure' | 'account-deletion';

export type PolicyInline =
  | { type: 'text'; value: string }
  | { type: 'strong'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; label: string; href: string }
  | { type: 'break' };

export type PolicyBlock =
  | { type: 'heading'; level: 2; content: PolicyInline[] }
  | { type: 'paragraph'; content: PolicyInline[] }
  | { type: 'table'; headers: PolicyInline[][]; rows: PolicyInline[][][] };

export interface PublicPolicyDocument {
  title: string;
  blocks: PolicyBlock[];
}

function parseInline(source: string): PolicyInline[] {
  const parts: PolicyInline[] = [];
  const tokenPattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|`[^`]+`|\n)/g;
  let cursor = 0;
  for (const match of source.matchAll(tokenPattern)) {
    const index = match.index;
    if (index > cursor) parts.push({ type: 'text', value: source.slice(cursor, index) });
    const token = match[0];
    if (token === '\n') {
      parts.push({ type: 'break' });
    } else if (token.startsWith('**')) {
      parts.push({ type: 'strong', value: token.slice(2, -2) });
    } else if (token.startsWith('`')) {
      parts.push({ type: 'code', value: token.slice(1, -1) });
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (!link) throw new Error(`Unsupported policy inline token: ${token}`);
      parts.push({ type: 'link', label: link[1]!, href: link[2]! });
    }
    cursor = index + token.length;
  }
  if (cursor < source.length) parts.push({ type: 'text', value: source.slice(cursor) });
  return parts;
}

function tableCells(line: string): PolicyInline[][] {
  return line
    .slice(1, -1)
    .split('|')
    .map((cell) => parseInline(cell.trim()));
}

function paragraphSource(lines: string[]): string {
  return lines
    .map((line, index) => {
      const hardBreak = line.endsWith('  ') || line.endsWith('\\');
      const value = line.endsWith('  ')
        ? line.slice(0, -2)
        : line.endsWith('\\')
          ? line.slice(0, -1)
          : line;
      return index === lines.length - 1 ? value : `${value}${hardBreak ? '\n' : ' '}`;
    })
    .join('');
}

export function parsePublicPolicy(source: string): PublicPolicyDocument {
  const lines = source.replaceAll('\r\n', '\n').trim().split('\n');
  const titleLine = lines.shift();
  if (!titleLine?.startsWith('# ')) throw new Error('Policy source must begin with one title.');
  const blocks: PolicyBlock[] = [];

  for (let index = 0; index < lines.length;) {
    const line = lines[index]!;
    if (!line.trim()) {
      index += 1;
      continue;
    }
    if (line.startsWith('## ')) {
      blocks.push({ type: 'heading', level: 2, content: parseInline(line.slice(3)) });
      index += 1;
      continue;
    }
    if (line.startsWith('|')) {
      const header = tableCells(line);
      const separator = lines[index + 1];
      if (!separator || !/^\|(?:\s*:?-+:?\s*\|)+$/.test(separator)) {
        throw new Error(`Policy table is missing its separator after: ${line}`);
      }
      const rows: PolicyInline[][][] = [];
      index += 2;
      while (index < lines.length && lines[index]!.startsWith('|')) {
        const row = tableCells(lines[index]!);
        if (row.length !== header.length) throw new Error('Policy table row width changed.');
        rows.push(row);
        index += 1;
      }
      blocks.push({ type: 'table', headers: header, rows });
      continue;
    }
    const paragraph: string[] = [];
    while (
      index < lines.length &&
      lines[index]!.trim() &&
      !lines[index]!.startsWith('## ') &&
      !lines[index]!.startsWith('|')
    ) {
      paragraph.push(lines[index]!);
      index += 1;
    }
    blocks.push({ type: 'paragraph', content: parseInline(paragraphSource(paragraph)) });
  }

  return { title: titleLine.slice(2), blocks };
}

const policySources: Record<PolicySlug, string> = {
  privacy: privacySource,
  terms: termsSource,
  'parent-notice': parentNoticeSource,
  'tracking-disclosure': trackingDisclosureSource,
  'account-deletion': accountDeletionSource,
};

export const PUBLIC_POLICY_DOCUMENTS = Object.fromEntries(
  Object.entries(policySources).map(([slug, source]) => [slug, parsePublicPolicy(source)]),
) as Record<PolicySlug, PublicPolicyDocument>;

export function canonicalPolicyDocument(document: PublicPolicyDocument): string {
  return JSON.stringify(document);
}

function digest(document: PublicPolicyDocument): string {
  return bytesToHex(sha256(new TextEncoder().encode(canonicalPolicyDocument(document))));
}

const canonicalIdentity = {
  version: CURRENT_PUBLIC_POLICY_IDENTITY.version,
  effectiveAt: CURRENT_PUBLIC_POLICY_IDENTITY.effectiveAt,
  termsSha256: digest(PUBLIC_POLICY_DOCUMENTS.terms),
  privacySha256: digest(PUBLIC_POLICY_DOCUMENTS.privacy),
} as const;

if (
  canonicalIdentity.termsSha256 !== CURRENT_PUBLIC_POLICY_IDENTITY.termsSha256 ||
  canonicalIdentity.privacySha256 !== CURRENT_PUBLIC_POLICY_IDENTITY.privacySha256
) {
  throw new Error('Canonical public policy content does not match its published identity.');
}

export { CURRENT_PUBLIC_POLICY_IDENTITY };

export function isPolicySlug(value: string): value is PolicySlug {
  return value in PUBLIC_POLICY_DOCUMENTS;
}

export function matchesCurrentPublicPolicy(identity: {
  version: string;
  effectiveAt: string;
  termsSha256: string;
  privacySha256: string;
}): boolean {
  return (
    identity.version === CURRENT_PUBLIC_POLICY_IDENTITY.version &&
    identity.effectiveAt === CURRENT_PUBLIC_POLICY_IDENTITY.effectiveAt &&
    identity.termsSha256 === CURRENT_PUBLIC_POLICY_IDENTITY.termsSha256 &&
    identity.privacySha256 === CURRENT_PUBLIC_POLICY_IDENTITY.privacySha256
  );
}
